/** Owners of several shops: choosing one, switching, copying products between them, listing them on the menu. */
const shops = {
  en: {
    switcher: {
      open: "Switch shop",
      title: "Your shops",
      current: "Open now",
      suspended: "Suspended",
      opening: "Opening {name}…",
      intro: "Each shop has its own products, till and figures. This phone takes one device slot in each shop it opens.",
      unsentSales: {
        one: "{count} sale rung up offline is still waiting to be sent from this shop's till. Open the register and let it send before switching.",
        other: "{count} sales rung up offline are still waiting to be sent from this shop's till. Open the register and let them send before switching.",
      },
      deviceLimit: "All {limit} device slots of {name} are in use. Free one to open it on this phone:",
      free: "Free",
      lastUsed: "last used {when}",
      failed: "Could not open that shop.",
    },
    open: {
      button: "Open another shop",
      title: "Open another shop",
      room: {
        one: "Your subscription covers {limit} shops: there is room for {count} more.",
        other: "Your subscription covers {limit} shops: there is room for {count} more.",
      },
      intro: "The new shop is added to your subscription at no extra cost. Same account, same till PIN. It has its own products, stock, till and figures.",
      next: "Next you give it its name and details, then pick its products: from the catalogue or copied from {name}.",
      device: "This phone becomes one of the new shop's devices.",
      confirm: "Open the new shop",
      opening: "Opening…",
      failed: "Could not open a new shop.",
    },
    pick: {
      title: "Which shop is this device for?",
      intro: "You work in more than one shop. Pick the one to open here — you can switch later from the shop's name at the top of the menu.",
      suspended: "suspended",
    },
    copy: {
      button: "Copy from my other shop",
      title: "Copy products from another shop",
      intro: "Names, pictures, details and ranges are copied. Stock is not: count it here once the products arrive.",
      from: "Copy from",
      search: "Search a product or a brand",
      all: "Every category",
      withPrices: "Copy the selling prices too",
      withCosts: "Copy the buy prices too",
      withoutPricesHint: "Without prices, copies wait as drafts until you price them.",
      selectAll: "Select all on this page",
      alreadyHere: "Already here",
      draft: "draft",
      copy: { one: "Copy {count} product", other: "Copy {count} products" },
      copyEverything: "Copy every product",
      copying: "Copying…",
      done: { one: "{count} product copied.", other: "{count} products copied." },
      skipped: { one: "{count} was already here.", other: "{count} were already here." },
      empty: "Nothing to copy here.",
      noOtherShop: "You have no other shop to copy from.",
      failed: "Could not copy those products.",
      loadMore: "Show more",
    },
    sister: {
      title: "Your other shops on the menu",
      toggle: "List my other shops on this menu",
      hint: "Customers see your other shops, nearest first, with their opening hours and directions. A shop is listed only if this is switched on there too.",
      location: "Where the shop is",
      locationHint: "Used to show customers the nearest shop. Press the button while you are inside the shop.",
      useHere: "I am in the shop now",
      locating: "Finding where you are…",
      located: "Location saved: {lat}, {lng}",
      clear: "Remove the location",
      none: "No location yet",
      denied: "The phone did not share its location. Allow it for this site and try again.",
      unavailable: "This browser cannot tell where it is.",
    },
    pack: {
      title: "One subscription for {count} shops",
      paidBy: "It is paid with {name}: one payment covers {shops}.",
      thisPays: "This shop pays for the pack: one payment covers {shops}.",
    },
  },

  fr: {
    switcher: {
      open: "Changer de boutique",
      title: "Vos boutiques",
      current: "Ouverte",
      suspended: "Suspendue",
      opening: "Ouverture de {name}…",
      intro: "Chaque boutique a ses propres produits, sa caisse et ses chiffres. Ce téléphone occupe un emplacement d'appareil dans chaque boutique qu'il ouvre.",
      unsentSales: {
        one: "{count} vente enregistrée hors ligne attend encore d'être envoyée depuis la caisse de cette boutique. Ouvrez la caisse et laissez-la partir avant de changer.",
        other: "{count} ventes enregistrées hors ligne attendent encore d'être envoyées depuis la caisse de cette boutique. Ouvrez la caisse et laissez-les partir avant de changer.",
      },
      deviceLimit: "Les {limit} emplacements d'appareil de {name} sont occupés. Libérez-en un pour l'ouvrir sur ce téléphone :",
      free: "Libérer",
      lastUsed: "utilisé {when}",
      failed: "Impossible d'ouvrir cette boutique.",
    },
    open: {
      button: "Ouvrir une autre boutique",
      title: "Ouvrir une autre boutique",
      room: {
        one: "Votre abonnement couvre {limit} boutiques : il reste de la place pour {count} de plus.",
        other: "Votre abonnement couvre {limit} boutiques : il reste de la place pour {count} de plus.",
      },
      intro: "La nouvelle boutique s'ajoute à votre abonnement sans frais en plus. Même compte, même code PIN de caisse. Elle a ses propres produits, stock, caisse et chiffres.",
      next: "Ensuite vous lui donnez son nom et ses coordonnées, puis choisissez ses produits : dans le catalogue ou copiés depuis {name}.",
      device: "Ce téléphone devient un des appareils de la nouvelle boutique.",
      confirm: "Ouvrir la nouvelle boutique",
      opening: "Ouverture…",
      failed: "Impossible d'ouvrir une nouvelle boutique.",
    },
    pick: {
      title: "Pour quelle boutique est cet appareil ?",
      intro: "Vous travaillez dans plusieurs boutiques. Choisissez celle à ouvrir ici — vous pourrez changer ensuite depuis le nom de la boutique en haut du menu.",
      suspended: "suspendue",
    },
    copy: {
      button: "Copier depuis mon autre boutique",
      title: "Copier des produits d'une autre boutique",
      intro: "Les noms, photos, détails et gammes sont copiés. Le stock ne l'est pas : comptez-le ici à l'arrivée des produits.",
      from: "Copier depuis",
      search: "Chercher un produit ou une marque",
      all: "Toutes les catégories",
      withPrices: "Copier aussi les prix de vente",
      withCosts: "Copier aussi les prix d'achat",
      withoutPricesHint: "Sans prix, les copies restent en brouillon jusqu'à ce que vous les fixiez.",
      selectAll: "Tout sélectionner sur cette page",
      alreadyHere: "Déjà ici",
      draft: "brouillon",
      copy: { one: "Copier {count} produit", other: "Copier {count} produits" },
      copyEverything: "Copier tous les produits",
      copying: "Copie…",
      done: { one: "{count} produit copié.", other: "{count} produits copiés." },
      skipped: { one: "{count} était déjà ici.", other: "{count} étaient déjà ici." },
      empty: "Rien à copier ici.",
      noOtherShop: "Vous n'avez pas d'autre boutique d'où copier.",
      failed: "Impossible de copier ces produits.",
      loadMore: "Voir plus",
    },
    sister: {
      title: "Vos autres boutiques sur le menu",
      toggle: "Afficher mes autres boutiques sur ce menu",
      hint: "Les clients voient vos autres boutiques, la plus proche d'abord, avec leurs horaires et l'itinéraire. Une boutique n'apparaît que si ce réglage est activé chez elle aussi.",
      location: "Emplacement de la boutique",
      locationHint: "Sert à montrer aux clients la boutique la plus proche. Appuyez sur le bouton quand vous êtes dans la boutique.",
      useHere: "Je suis dans la boutique",
      locating: "Recherche de votre position…",
      located: "Position enregistrée : {lat}, {lng}",
      clear: "Retirer la position",
      none: "Pas encore de position",
      denied: "Le téléphone n'a pas partagé sa position. Autorisez-la pour ce site et réessayez.",
      unavailable: "Ce navigateur ne peut pas donner sa position.",
    },
    pack: {
      title: "Un abonnement pour {count} boutiques",
      paidBy: "Il est payé avec {name} : un seul paiement couvre {shops}.",
      thisPays: "Cette boutique paie le pack : un seul paiement couvre {shops}.",
    },
  },

  ar: {
    switcher: {
      open: "تبديل المتجر",
      title: "متاجرك",
      current: "مفتوح الآن",
      suspended: "موقوف",
      opening: "جارٍ فتح {name}…",
      intro: "لكل متجر منتجاته وصندوقه وأرقامه. يشغل هذا الهاتف خانة جهاز في كل متجر يفتحه.",
      unsentSales: {
        zero: "لا توجد مبيعات بانتظار الإرسال.",
        one: "عملية بيع واحدة مسجّلة دون اتصال ما زالت بانتظار الإرسال من صندوق هذا المتجر. افتح الصندوق ودعها تُرسل قبل التبديل.",
        two: "عمليتا بيع مسجّلتان دون اتصال ما زالتا بانتظار الإرسال من صندوق هذا المتجر. افتح الصندوق ودعهما تُرسلان قبل التبديل.",
        few: "{count} عمليات بيع مسجّلة دون اتصال ما زالت بانتظار الإرسال من صندوق هذا المتجر. افتح الصندوق ودعها تُرسل قبل التبديل.",
        many: "{count} عملية بيع مسجّلة دون اتصال ما زالت بانتظار الإرسال من صندوق هذا المتجر. افتح الصندوق ودعها تُرسل قبل التبديل.",
        other: "{count} عملية بيع مسجّلة دون اتصال ما زالت بانتظار الإرسال من صندوق هذا المتجر. افتح الصندوق ودعها تُرسل قبل التبديل.",
      },
      deviceLimit: "كل خانات الأجهزة الـ{limit} في {name} مشغولة. حرّر واحدة لفتحه على هذا الهاتف:",
      free: "تحرير",
      lastUsed: "آخر استخدام {when}",
      failed: "تعذّر فتح هذا المتجر.",
    },
    open: {
      button: "افتح متجرًا آخر",
      title: "افتح متجرًا آخر",
      room: {
        zero: "يغطي اشتراكك {limit} متاجر: لا مكان لمتجر آخر.",
        one: "يغطي اشتراكك {limit} متاجر: يتّسع لمتجر واحد إضافي.",
        two: "يغطي اشتراكك {limit} متاجر: يتّسع لمتجرين إضافيين.",
        few: "يغطي اشتراكك {limit} متاجر: يتّسع لـ{count} متاجر إضافية.",
        many: "يغطي اشتراكك {limit} متجرًا: يتّسع لـ{count} متجرًا إضافيًا.",
        other: "يغطي اشتراكك {limit} متجرًا: يتّسع لـ{count} متجر إضافي.",
      },
      intro: "يُضاف المتجر الجديد إلى اشتراكك دون تكلفة إضافية. الحساب نفسه ورمز PIN الصندوق نفسه. له منتجاته ومخزونه وصندوقه وأرقامه.",
      next: "بعدها تعطيه اسمه وبياناته، ثم تختار منتجاته: من الكتالوج أو منسوخة من {name}.",
      device: "يصبح هذا الهاتف أحد أجهزة المتجر الجديد.",
      confirm: "افتح المتجر الجديد",
      opening: "جارٍ الفتح…",
      failed: "تعذّر فتح متجر جديد.",
    },
    pick: {
      title: "لأيّ متجر هذا الجهاز؟",
      intro: "أنت تعمل في أكثر من متجر. اختر المتجر الذي تفتحه هنا — يمكنك التبديل لاحقًا من اسم المتجر أعلى القائمة.",
      suspended: "موقوف",
    },
    copy: {
      button: "نسخ من متجري الآخر",
      title: "نسخ منتجات من متجر آخر",
      intro: "تُنسخ الأسماء والصور والتفاصيل والتشكيلات. أمّا المخزون فلا: عُدّه هنا عند وصول المنتجات.",
      from: "النسخ من",
      search: "ابحث عن منتج أو علامة",
      all: "كل الفئات",
      withPrices: "انسخ أسعار البيع أيضًا",
      withCosts: "انسخ أسعار الشراء أيضًا",
      withoutPricesHint: "دون أسعار، تبقى النسخ مسودّات حتى تحدّد أسعارها.",
      selectAll: "تحديد الكل في هذه الصفحة",
      alreadyHere: "موجود هنا",
      draft: "مسودّة",
      copy: {
        zero: "نسخ {count} منتج",
        one: "نسخ منتج واحد",
        two: "نسخ منتجين",
        few: "نسخ {count} منتجات",
        many: "نسخ {count} منتجًا",
        other: "نسخ {count} منتج",
      },
      copyEverything: "نسخ كل المنتجات",
      copying: "جارٍ النسخ…",
      done: {
        zero: "لم يُنسخ أي منتج.",
        one: "نُسخ منتج واحد.",
        two: "نُسخ منتجان.",
        few: "نُسخت {count} منتجات.",
        many: "نُسخ {count} منتجًا.",
        other: "نُسخ {count} منتج.",
      },
      skipped: {
        zero: "لا شيء كان موجودًا.",
        one: "واحد كان موجودًا هنا.",
        two: "اثنان كانا موجودين هنا.",
        few: "{count} كانت موجودة هنا.",
        many: "{count} كانت موجودة هنا.",
        other: "{count} كانت موجودة هنا.",
      },
      empty: "لا شيء للنسخ هنا.",
      noOtherShop: "ليس لديك متجر آخر للنسخ منه.",
      failed: "تعذّر نسخ هذه المنتجات.",
      loadMore: "عرض المزيد",
    },
    sister: {
      title: "متاجرك الأخرى في القائمة",
      toggle: "اعرض متاجري الأخرى في هذه القائمة",
      hint: "يرى الزبائن متاجرك الأخرى، الأقرب أولًا، مع أوقات العمل والاتجاهات. لا يظهر متجر إلا إذا فُعّل هذا الخيار فيه أيضًا.",
      location: "موقع المتجر",
      locationHint: "يُستخدم لعرض أقرب متجر للزبائن. اضغط الزر وأنت داخل المتجر.",
      useHere: "أنا في المتجر الآن",
      locating: "جارٍ تحديد موقعك…",
      located: "حُفظ الموقع: {lat}، {lng}",
      clear: "إزالة الموقع",
      none: "لا يوجد موقع بعد",
      denied: "لم يشارك الهاتف موقعه. اسمح بذلك لهذا الموقع ثم أعد المحاولة.",
      unavailable: "لا يستطيع هذا المتصفح تحديد موقعه.",
    },
    pack: {
      title: "اشتراك واحد لـ{count} متاجر",
      paidBy: "يُدفع مع {name}: دفعة واحدة تغطي {shops}.",
      thisPays: "هذا المتجر يدفع الباقة: دفعة واحدة تغطي {shops}.",
    },
  },
};

export default shops;
